import { useTranslation } from 'react-i18next';
import { Field, FieldArray, FieldProps, Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import React, { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { ActionButton, ChoiceGroup, Dropdown, Label, Stack, TextField, Toggle } from '@fluentui/react';
import { useProviderType } from '../../../../../shared/hooks/useProviderType';

import { getFieldMetaError } from '../../../../error/utils/getFieldMetaError';
import FormActionButtons from '../../../../common/components/actions/form-action-buttons/FormActionButtons';
import { CallTypes, IActionUrlForm, TriggerValues, UrlTargetTypes } from '../../types/ActionUrl';
import { ParameterItem } from './ParameterFormItem';
import { LinkPreview } from '../LinkPreview';
import { getActionUrlFormValidationScheme } from './formValidationScheme';
import { InfoTooltip } from '../../../../common/components/tooltip/InfoTooltip';
import { fieldStackTokens } from 'theme/tokens';

import styles from '../../styles/ActionUrl.module.css';

interface ActionUrlFormProps {
  initialState?: IActionUrlForm;
  disabledActions?: boolean;
  hideForm?: boolean;
  onSubmit: (value: IActionUrlForm, helpers: FormikHelpers<IActionUrlForm>) => void;
  deleteBtn?: ReactNode;
}

export const ActionUrlForm: FC<ActionUrlFormProps> = ({ initialState, hideForm, onSubmit, deleteBtn }) => {
  const { t } = useTranslation();
  const { isRingCentral } = useProviderType();

  const initialValues = useMemo<IActionUrlForm>(() => {
    return initialState || {
      enabled: false,
      title: '',
      url: '',
      parameters: [{
        parameterName: '',
        parameterValue: undefined,
        numberReplacement: [],
      }],
      trigger: '',
      callType: CallTypes.all_calls,
      urlTarget: UrlTargetTypes.tab,
    }
  }, [initialState]);

  const resetCallback = useCallback((_: IActionUrlForm, helpers: FormikHelpers<IActionUrlForm>) => {
    void helpers.setValues(initialValues, true);
  }, [initialValues]);

  const validationSchema = useMemo(() => getActionUrlFormValidationScheme(t), [t]);

  return (
    <Formik<IActionUrlForm>
      validateOnMount={false}
      validateOnChange={false}
      validateOnBlur={false}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={resetCallback}
      enableReinitialize={true}
    >
      {({ values, setFieldValue }) => {
        const isCallTypesSelectionDisabled = [TriggerValues.hangup, TriggerValues.transferred].includes(values.trigger as TriggerValues);

        return (
          <Form style={{ display: hideForm ? 'none' : 'block' }}>
            <CallTypeGuard isCallTypesDisabled={isCallTypesSelectionDisabled} />
            <Stack tokens={fieldStackTokens}>
              <div className={styles.flexEndContainer} style={{ gap: 8 }}>
                <Field name="enabled">
                  {({ field }: FieldProps<boolean>) => (
                    <Toggle
                      label={t('enabled')}
                      inlineLabel
                      checked={field.value}
                      onChange={(_, checked) => setFieldValue("enabled", checked)}
                    />
                  )}
                </Field>
                {deleteBtn && deleteBtn}
              </div>

              <Field name="title">
                {({ field, meta }: FieldProps<string, IActionUrlForm>) => (
                  <TextField
                    required
                    label={t("common:title")}
                    errorMessage={getFieldMetaError(meta)}
                    {...field}
                  />
                )}
              </Field>

              <Field name="url">
                {({ field, meta }: FieldProps<string, IActionUrlForm>) => (
                  <TextField
                    label={t("common:url")}
                    required
                    errorMessage={getFieldMetaError(meta)}
                    {...field}
                  />
                )}
              </Field>

              <FieldArray name="parameters">
                {({ remove, push }) => {
                  return (
                    <div>
                      {values.parameters.map((_, index) => (
                        <ParameterItem
                          key={index}
                          index={index}
                          remove={remove}
                        />
                      ))}

                      <div className={styles.flexEndContainer}>
                        <ActionButton
                          iconProps={{ iconName: 'Add' }}
                          aria-label={t('common:addParameter')}
                          onClick={() => push({
                            parameterName: '',
                            parameterValue: undefined,
                          })}
                        >
                          {t('common:addParameter')}
                        </ActionButton>
                      </div>
                    </div>
                  )
                }}
              </FieldArray>

              <Field name="trigger">
                {({ field, meta }: FieldProps<string>) => (
                  <Dropdown
                    required
                    placeholder={t('common:selectValue')}
                    label={t('common:triggerValue')}
                    selectedKey={field.value}
                    onChange={(_, option) => setFieldValue("trigger", option?.key)}
                    options={[
                      { key: TriggerValues.ringing, text: t('common:ringing') },
                      { key: TriggerValues.answered, text: t('common:answered') },
                      { key: TriggerValues.hangup, text: t('common:hangup') },
                      ...(isRingCentral
                        ? [{ key: TriggerValues.transferred, text: t('common:transferred') }]
                        : []),
                    ]}
                    errorMessage={getFieldMetaError(meta)}
                  />
                )}
              </Field>

              {isRingCentral && (
                <Field name="callType">
                  {({ field }: FieldProps<string>) => (
                    <>
                      <Label id="callTypes">
                        <Stack horizontal verticalAlign="center">
                          <span>{t('common:callTypes')}&nbsp;</span>
                          {isCallTypesSelectionDisabled && (
                            <div style={{ position: 'relative', top: 4 }}>
                              <InfoTooltip infoContent={t('common:callTypesHint')} />
                            </div>
                          )}
                        </Stack>
                      </Label>
                      <ChoiceGroup
                        ariaLabelledBy={'callTypes'}
                        selectedKey={field.value}
                        disabled={isCallTypesSelectionDisabled}
                        onChange={(_, option) =>
                          setFieldValue("callType", option?.key)
                        }
                        options={[
                          { key: CallTypes.all_calls, text: t('common:allCalls') },
                          { key: CallTypes.queue_calls, text: t('common:queueCalls') },
                        ]}
                      />
                    </>
                  )}
                </Field>
              )}
              <LinkPreview />
            </Stack>
            <FormActionButtons />
          </Form>
        );
      }}
    </Formik>
  );
};

const CallTypeGuard: FC<{ isCallTypesDisabled: boolean }> = ({ isCallTypesDisabled }) => {
  const { values, setFieldValue } = useFormikContext<IActionUrlForm>();

  useEffect(() => {
    if (isCallTypesDisabled && values.callType !== CallTypes.all_calls) {
      void setFieldValue('callType', CallTypes.all_calls);
    }
  }, [isCallTypesDisabled, values.callType, setFieldValue]);

  return null;
};